import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer, createMigrate } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import authReducer from '@features/auth/store/authSlice';
import dealerConfigReducer from '@features/dealers/store/dealerConfigSlice';
import deviceReducer from './slices/deviceSlice';
import quoteReducer from '@features/quote/store/quoteSlice';
import { PERSIST_VERSION, persistMigrations } from './persistMigrations';

// One-shot migration: scrub any pre-existing `auth` payload from persist:root.
// Runs synchronously on module load, before persistStore is called.
// Idempotent — once the auth field is removed, subsequent runs are no-ops.
// Remove this block one release cycle after deployment.
try {
    const raw = localStorage.getItem('persist:root');
    if (raw) {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        if (parsed && typeof parsed === 'object' && 'auth' in parsed) {
            delete parsed.auth;
            localStorage.setItem('persist:root', JSON.stringify(parsed));
        }
    }
} catch {
    // Corrupt or missing entry. Leave it alone; redux-persist will recover.
}

// One-shot migration: scrub `county` from a persisted quote.selectedDealer (Epic 7.1.9).
// Removing the type member is compile-time only — a dealer snapshot rehydrated from
// before this release would still carry county into the quote create/update payload.
// Remove this block one release cycle after deployment.
try {
    const raw = localStorage.getItem('persist:root');
    if (raw) {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        const quoteRaw = parsed.quote;
        if (typeof quoteRaw === 'string') {
            const quote = JSON.parse(quoteRaw) as { selectedDealer?: unknown };
            const dealer = quote?.selectedDealer;
            if (dealer && typeof dealer === 'object' && 'county' in dealer) {
                delete (dealer as Record<string, unknown>).county;
                parsed.quote = JSON.stringify(quote);
                localStorage.setItem('persist:root', JSON.stringify(parsed));
            }
        }
    }
} catch {
    // Corrupt or missing entry. Leave it alone; redux-persist will recover.
}

// One-shot: discard a quote still keyed on id (row identity renamed to instanceId).
// Self-limiting — redux-persist writes state back keyed on instanceId, so this then no-ops.
try {
    const raw = localStorage.getItem('persist:root');
    if (raw) {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        const quoteRaw = parsed.quote;
        if (typeof quoteRaw === 'string') {
            const quote = JSON.parse(quoteRaw) as {
                configuredAddOns?: unknown;
                configuredServices?: unknown;
            };
            const hasStaleRow = (rows: unknown): boolean =>
                Array.isArray(rows) &&
                rows.some(
                    (row) => row && typeof row === 'object' && 'id' in row && !('instanceId' in row)
                );
            if (hasStaleRow(quote?.configuredAddOns) || hasStaleRow(quote?.configuredServices)) {
                delete parsed.quote;
                localStorage.setItem('persist:root', JSON.stringify(parsed));
            }
        }
    }
} catch {
    // Corrupt or missing entry. Leave it alone; redux-persist will recover.
}

// UNCHANGED — dealerConfig is deliberately NOT persisted (plan D5). Dealer config
// is refetched per session rather than going stale in localStorage with no
// invalidation path; the useDealerConfig() self-heal makes the post-reload
// refetch converge correctly.
export const persistConfig = {
    key: 'root',
    storage,
    whitelist: ['device', 'quote'],
    version: PERSIST_VERSION,
    migrate: createMigrate(persistMigrations),
};

const rootReducer = combineReducers({
    auth: authReducer,
    device: deviceReducer,
    quote: quoteReducer,
    dealerConfig: dealerConfigReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [
                    'persist/PERSIST',
                    'persist/REHYDRATE',
                    'persist/PAUSE',
                    'persist/FLUSH',
                    'persist/PURGE',
                    'persist/REGISTER',
                ],
            },
        }),
    devTools: process.env.NODE_ENV !== 'production', // Enable Redux DevTools
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
